/**
 * Shell — หัวข้อ, ชิปเลือกชุด, หน้าฝึก, สถิติ, ตั้งค่า
 */
import type { Card, DeckId } from '@/core/types'
import { DECKS, filterCards, getDeck } from '@/decks'
import type { Deck, ModeId } from '@/decks/types'
import { isLessonMode, isPickerMode, MODE_HINT, MODE_LABEL } from '@/decks/types'
import { downloadBackup, importBackup, readBackupFile } from '@/core/backup'
import { clearAll } from '@/core/db'
import { syncNow } from '@/core/sync'
import { hasJapaneseVoice } from '@/audio/play'
import { REASON_LABEL, type Weakness } from '@/core/weakness'
import { FONTS } from './fonts'
import { Session } from './session'
import {
  applyTheme,
  cycleTheme,
  getPicked,
  saveSettings,
  setPicked,
  settings,
  THEME_ICON,
} from './settings'
import { shouldUseKeypad } from './keypad'

/**
 * โหมดที่ควรใช้กับ deck นี้ — ค่าที่จำไว้ต้องยังเป็นโหมดที่ deck รองรับอยู่จริง
 *
 * โหมดถูกลบหรือเปลี่ยนชื่อได้ (audio-to-romaji → audio-to-glyph) แต่ค่าที่ผู้ใช้
 * เลือกค้างไว้อยู่ใน localStorage ข้ามการอัปเดตมา — ปล่อยผ่านแล้วจะได้หน้าที่
 * ไม่มีชิปไหนถูกเลือก คำใบ้ว่าง และไม่มีอะไรบอกว่าตอนนี้อยู่โหมดอะไร
 */
function modeFor(deck: Deck): ModeId | undefined {
  const saved = settings.mode[deck.id]
  return saved && deck.modes.includes(saved) ? saved : deck.modes[0]
}

export class App {
  private deck!: Deck
  private session!: Session
  private els!: {
    headJa: HTMLElement
    headTh: HTMLElement
    count: HTMLElement
    theme: HTMLButtonElement
    controls: HTMLElement
    sessionHost: HTMLElement
    panel: HTMLElement
    scope: HTMLElement
    picker: HTMLDialogElement
    bar: HTMLElement
    hint: HTMLElement
    statRow: HTMLElement
    stTotal: HTMLElement
    stAcc: HTMLElement
    stStreak: HTMLElement
    stBest: HTMLElement
  }

  constructor(private root: HTMLElement) {}

  async mount(): Promise<void> {
    applyTheme()
    this.render()
    // ตอบทีไรจุดอ่อนเปลี่ยนทันที — ปุ่มเก็บตกต้องตามให้ทัน ไม่ใช่รอ reload
    this.session = new Session(
      this.els.sessionHost,
      () => {
        this.refreshStats()
        this.renderScope()
      },
      (mode) => {
        void this.session.setMode(mode).then(() => {
          this.renderControls()
          this.renderScope()
          this.refreshStats()
        })
      },
      // จอจบของโหมดอัตโนมัติเสนอชุดถัดไปให้ — กดแล้วต้องพาไปได้เลย
      (id) => {
        void this.selectDeck(id)
      },
    )
    await this.selectDeck(settings.deck)
    this.setupAutoSync()
  }

  // ---------- โครงหน้า ----------

  private render(): void {
    this.root.replaceChildren()

    const head = document.createElement('header')
    head.className = 'head w'
    head.innerHTML = `
      <div>
        <h1 class="head-jp" id="headJa"></h1>
        <span class="head-th" id="headTh"></span>
      </div>
      <div class="head-right">
        <div class="head-count" id="count"></div>
        <button class="icon-btn" id="theme" type="button" title="สลับธีม"></button>
        <button class="icon-btn" id="gear" type="button" title="ตั้งค่า">⚙</button>
      </div>`

    // เหลือแค่ deck กับโหมด — ตัวกรองย้ายลงไปอยู่ใต้ที่ฝึก
    const controls = document.createElement('section')
    controls.className = 'controls w'

    const panel = document.createElement('section')
    panel.className = 'panel w'
    panel.hidden = true

    const sessionHost = document.createElement('div')
    sessionHost.className = 'w'
    sessionHost.style.display = 'contents'

    // แถบสรุปว่ากำลังฝึกชุดไหน + ปุ่มเปิด dialog เลือกชุด
    const scope = document.createElement('section')
    scope.className = 'scope w'

    const picker = document.createElement('dialog')
    picker.className = 'picker'

    const stats = document.createElement('section')
    stats.className = 'stats w'
    stats.innerHTML = `
      <div class="bar"><div class="bar-fill" id="bar"></div></div>
      <div class="stat-row" id="statRow">
        <div class="stat"><span class="stat-k">ตอบแล้ว</span><span class="stat-v" id="stTotal">0</span></div>
        <div class="stat"><span class="stat-k">ถูก</span><span class="stat-v" id="stAcc">—</span></div>
        <div class="stat"><span class="stat-k">ติดต่อกัน</span><span class="stat-v" id="stStreak">0</span></div>
        <div class="stat"><span class="stat-k">ยาวสุด</span><span class="stat-v" id="stBest">0</span></div>
      </div>`

    const foot = document.createElement('footer')
    foot.className = 'foot w'
    foot.innerHTML = '<span id="hint"></span>'

    // ของหลักอยู่บน ตัวกรองอยู่ล่าง — ไม่ให้ปุ่มดันช่องคัดอักษรตกจอ
    this.root.append(head, controls, panel, sessionHost, scope, stats, foot, picker)

    const q = <T extends HTMLElement>(id: string): T => {
      const el = this.root.querySelector<T>(`#${id}`)
      if (!el) throw new Error(`missing #${id}`)
      return el
    }

    this.els = {
      headJa: q('headJa'),
      headTh: q('headTh'),
      count: q('count'),
      theme: q<HTMLButtonElement>('theme'),
      controls,
      sessionHost,
      panel,
      scope,
      picker,
      bar: q('bar'),
      hint: q('hint'),
      statRow: q('statRow'),
      stTotal: q('stTotal'),
      stAcc: q('stAcc'),
      stStreak: q('stStreak'),
      stBest: q('stBest'),
    }

    this.els.theme.textContent = THEME_ICON[settings.theme]
    this.els.theme.addEventListener('click', () => {
      this.els.theme.textContent = THEME_ICON[cycleTheme()]
    })
    q<HTMLButtonElement>('gear').addEventListener('click', () => {
      panel.hidden = !panel.hidden
      if (!panel.hidden) this.renderPanel()
    })
  }

  // ---------- เลือก deck ----------

  private async selectDeck(id: DeckId): Promise<void> {
    const deck = getDeck(id) ?? DECKS[0]
    if (!deck) throw new Error('ไม่มี deck')
    this.deck = deck
    settings.deck = deck.id
    saveSettings()

    this.els.headJa.textContent = deck.titleJa
    this.els.headTh.textContent = deck.titleTh

    if (deck.ensureLoaded && deck.cards.length === 0) {
      this.els.headTh.textContent = 'กำลังโหลดข้อมูลคันจิ…'
      try {
        await deck.ensureLoaded()
        this.els.headTh.textContent = deck.titleTh
      } catch (err) {
        this.els.headTh.textContent =
          err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ'
        return
      }
    }

    this.renderControls()
    const mode = modeFor(deck)
    if (!mode) throw new Error('deck ไม่มีโหมด')
    // ต้อง start ก่อน — renderScope อ่านจุดอ่อนจาก session ซึ่งยังไม่รู้จัก deck จนกว่าจะ start
    await this.session.start(deck, mode)
    this.renderScope()
    this.refreshStats()
  }

  // ---------- ชิป ----------

  /**
   * เปลี่ยนตัวกรองแล้วต้องรอ session สร้าง pool ใหม่ก่อนค่อยอัปเดตตัวนับ
   * ไม่งั้นหัวข้อยังโชว์จำนวนของชุดเดิมจนกว่าจะตอบข้อแรก
   */
  private async refilter(): Promise<void> {
    await this.session.reload()
    this.renderControls()
    this.renderScope()
    if (this.els.picker.open) this.renderPicker()
    this.refreshStats()
  }

  private renderControls(): void {
    const c = this.els.controls
    c.replaceChildren()

    // แถวเลือก deck — ซ่อนถ้ามี deck เดียว
    if (DECKS.length > 1) {
      c.append(
        this.chipRow(
          'ชุดฝึก',
          DECKS.map((d) => ({
            tag: d.id,
            label: d.titleJa,
            pressed: d.id === this.deck.id,
            jp: true,
            onClick: () => void this.selectDeck(d.id),
          })),
        ),
      )
    }

    const currentMode = modeFor(this.deck)
    const chip = (m: ModeId) => ({
      tag: m,
      label: MODE_LABEL[m],
      pressed: m === currentMode,
      onClick: () => {
        void this.session.setMode(m).then(() => {
          this.renderControls()
          this.renderScope()
          this.refreshStats()
        })
      },
    })

    /*
     * สองแถวเพราะเป็นคนละคำถาม — แถวบนตอบว่า "เอาใบไหนมา" (ระบบจัดให้)
     * แถวล่างตอบว่า "ถามยังไง" (เจาะจงเอง) · รวมแถวเดียวแล้วต้องตัดสินใจทุกวัน
     * ว่าจะกดอันไหน ทั้งที่ 95% ของวันคำตอบคืออัตโนมัติ
     */
    const picker = this.deck.modes.filter(isPickerMode)
    const ask = this.deck.modes.filter((m) => !isPickerMode(m))
    if (picker.length > 0) c.append(this.chipRow('ฝึก', picker.map(chip)))
    if (ask.length > 0) c.append(this.chipRow(picker.length > 0 ? 'เจาะจง' : 'โหมด', ask.map(chip)))
  }

  // ---------- แถบบอกว่ากำลังฝึกชุดไหน ----------

  /**
   * สรุปเป็นประโยคเดียว ไม่ใช่ปุ่ม 40 อัน
   * ถ้าที่เลือกไว้บังเอิญตรงกับกลุ่มใดกลุ่มหนึ่งพอดี ก็เรียกชื่อกลุ่มนั้นแทนตัวเลขเปล่าๆ
   */
  private scopeLabel(): string {
    if (this.session.isProbing()) return 'กำลังทดสอบว่ารู้อะไรอยู่แล้วบ้าง'
    const mode = this.currentMode()
    // โหมดที่ระบบจัดใบให้ — บอกจำนวนในคิววันนี้ ไม่ใช่ขนาดของชุดทั้งหมด
    if (mode === 'auto') return `คิววันนี้ ${this.session.summary().total} ใบ`
    if (isLessonMode(mode)) {
      return `รอเรียน ${this.session.summary().total} ใบ`
    }
    // เปิดโหมดเก็บตกไว้แต่ไม่มีตัวอ่อนเลย = ไม่ได้กรองอะไร อย่าไปบอกว่าเก็บตก
    if (this.session.isWeakActive()) return `เก็บตก ${this.session.summary().total} ตัว`

    const ids = new Set(filterCards(this.deck, getPicked(this.deck.id)).map((c) => c.id))

    let scope = `${ids.size} ตัว`
    for (const g of this.deck.groups) {
      const inGroup = this.deck.cards.filter((c) => c.tags.includes(g.tag))
      if (inGroup.length === ids.size && inGroup.every((c) => ids.has(c.id))) {
        scope = `${g.label} · ${ids.size} ตัว`
        break
      }
    }

    // ชุดที่เลือกกับที่ฝึกได้จริงต่างกันตอนยังเรียนไม่ครบ — บอกทั้งสองไม่งั้นตัวเลขขัดกันเอง
    const usable = this.session.summary().total
    return usable < ids.size ? `${scope} · เรียนแล้ว ${usable}` : scope
  }

  private renderScope(): void {
    const s = this.els.scope
    s.replaceChildren()

    const text = document.createElement('span')
    text.className = 'scope-text'
    text.innerHTML = `ฝึกอยู่: <b>${this.scopeLabel()}</b>`

    const actions = document.createElement('span')
    actions.className = 'scope-actions'

    // โหมดเรียนไม่เกี่ยวกับจุดอ่อน — ใบพวกนั้นยังไม่เคยถูกถามด้วยซ้ำ
    if (!isLessonMode(this.currentMode())) {
      const weak = this.session.weakInSelection()
      const weakBtn = document.createElement('button')
      weakBtn.type = 'button'
      weakBtn.className = 'btn btn-ghost btn-weak'
      weakBtn.setAttribute('aria-pressed', String(settings.weakOnly))
      weakBtn.disabled = weak.length === 0 && !settings.weakOnly
      weakBtn.textContent = settings.weakOnly
        ? 'ออกจากโหมดเก็บตก'
        : weak.length > 0
          ? `เก็บตก ${weak.length} ตัว`
          : 'ยังไม่มีตัวที่ต้องเก็บตก'
      weakBtn.title = 'ฝึกเฉพาะตัวที่ผิดบ่อย เพิ่งผิด หรือตอบถูกแต่ยังช้า'
      weakBtn.addEventListener('click', () => {
        settings.weakOnly = !settings.weakOnly
        saveSettings()
        void this.refilter()
      })
      actions.append(weakBtn)
    }

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'btn btn-ghost'
    btn.textContent = 'เปลี่ยนชุด'
    btn.addEventListener('click', () => {
      this.renderPicker()
      this.els.picker.showModal()
    })

    actions.append(btn)
    s.append(text, actions)
  }

  private currentMode(): ModeId {
    return modeFor(this.deck) ?? 'recall'
  }

  // ---------- dialog เลือกชุด ----------

  private renderPicker(): void {
    const d = this.els.picker
    d.replaceChildren()

    // ยังไม่เคยแตะตาราง → แสดงชุดที่กำลังฝึกอยู่จริง ไม่ใช่ตารางว่างเปล่า
    const shown = new Set(filterCards(this.deck, getPicked(this.deck.id)).map((c) => c.id))

    const head = document.createElement('div')
    head.className = 'picker-head'
    const title = document.createElement('h2')
    title.textContent = 'เลือกตัวที่จะฝึก'
    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'icon-btn'
    close.textContent = '✕'
    close.addEventListener('click', () => d.close())
    head.append(title, close)

    const body = document.createElement('div')
    body.className = 'picker-body'

    const weakness = this.session.weaknessMap()
    const weakCount = [...weakness.values()].filter((w) => w.score > 0 && shown.has(w.cardId)).length
    if (weakCount > 0) {
      const legend = document.createElement('div')
      legend.className = 'picker-legend'
      legend.innerHTML =
        `<span class="dot dot-weak"></span> ยังไม่แน่น ${weakCount} ตัว` +
        ` · <span class="dot dot-ok"></span> แน่นแล้ว` +
        ` · <span class="dot dot-new"></span> ยังไม่เจอ`
      body.append(legend)
    }

    body.append(this.renderGrid(shown, weakness))

    const foot = document.createElement('div')
    foot.className = 'picker-foot'

    const count = document.createElement('span')
    count.className = 'picker-count'
    count.textContent = `เลือกไว้ ${shown.size} ตัว`

    const done = document.createElement('button')
    done.type = 'button'
    done.className = 'btn'
    done.textContent = 'เริ่มฝึก'
    done.addEventListener('click', () => d.close())
    foot.append(count, done)

    d.append(head, body, foot)
  }

  // ---------- ตารางเลือกทีละตัว (แบบ DJT) ----------

  private renderGrid(picked: Set<string>, weakness: Map<string, Weakness>): HTMLElement {
    const box = document.createElement('div')
    box.className = 'grid-box'

    const apply = (next: Set<string>): void => {
      setPicked(this.deck.id, next)
      void this.refilter()
    }

    const bar = document.createElement('div')
    bar.className = 'grid-bar'
    const all = document.createElement('button')
    all.type = 'button'
    all.className = 'chip'
    all.textContent = 'เลือกทุกตัว'
    all.addEventListener('click', () => apply(new Set(this.deck.cards.map((c) => c.id))))
    const none = document.createElement('button')
    none.type = 'button'
    none.className = 'chip'
    none.textContent = 'ล้างทั้งหมด'
    none.addEventListener('click', () => apply(new Set()))
    bar.append(all, none)
    box.append(bar)

    /*
     * ไม่ได้ตั้ง gridSection = ใช้ section แรกที่ deck มี — ไม่งั้นตัวกรองว่างเปล่า
     * ทั้งกล่องโดยไม่มีอะไรบอกว่าทำไม (deck คำศัพท์เจอตอนเพิ่มเข้ามาใหม่ ๆ)
     */
    const gridSection = this.deck.gridSection ?? this.deck.groups[0]?.section
    const rowGroups = this.deck.groups.filter((x) => x.section === gridSection)
    // ไม่มี gridSplit = บล็อกเดียว
    const blocks = this.deck.gridSplit
      ? this.deck.groups.filter((x) => x.section === this.deck.gridSplit)
      : [null]

    // deck ที่ไม่ได้บอกโครงตาราง (bushu) = ตารางเดียวเรียงแบบ wrap
    const subsets = this.deck.gridSubsets ?? [{ tags: [], label: '', slots: 0 }]

    for (const block of blocks) {
      for (const sub of subsets) {
        const inSub = (c: Card): boolean =>
          (sub.tags.length === 0 || sub.tags.some((t) => c.tags.includes(t))) &&
          (!block || c.tags.includes(block.tag))

        const cols = rowGroups
          .map((g) => ({ g, cards: this.deck.cards.filter((c) => c.tags.includes(g.tag) && inSub(c)) }))
          .filter((c) => c.cards.length > 0)
        if (cols.length === 0) continue

        const blockCards = cols.flatMap((c) => c.cards)

        /*
         * 3 ปุ่มแยก ไม่ใช่ toggle ตัวเดียว
         * toggle ต้องเดาสถานะ "เลือกครบหรือยัง" ซึ่งเดาผิดตลอดเมื่อมีการ์ด rare
         * ที่ไม่ได้ถูกเลือกโดยดีฟอลต์ — กดแล้วได้ผลที่ไม่มีใครคาด
         */
        const title = document.createElement('div')
        title.className = 'grid-block-title'
        const titleText = document.createElement('span')
        titleText.textContent = [block?.label, sub.label].filter(Boolean).join(' · ') || 'ทั้งหมด'

        const actions = document.createElement('span')
        actions.className = 'block-actions'
        const mkAction = (label: string, fn: () => void): HTMLButtonElement => {
          const b = document.createElement('button')
          b.type = 'button'
          b.className = 'block-toggle'
          b.textContent = label
          b.addEventListener('click', fn)
          return b
        }
        actions.append(
          mkAction('เฉพาะกลุ่มนี้', () => apply(new Set(blockCards.map((c) => c.id)))),
          mkAction('+ เพิ่ม', () => {
            const next = new Set(picked)
            for (const c of blockCards) next.add(c.id)
            apply(next)
          }),
          mkAction('− เอาออก', () => {
            const next = new Set(picked)
            for (const c of blockCards) next.delete(c.id)
            apply(next)
          }),
        )

        title.append(titleText, actions)
        box.append(title)

        // ไม่มี slot = ไม่ใช่ตาราง gojūon → เรียง wrap ธรรมดา (bushu)
        if (sub.slots === 0) {
          for (const { g, cards } of cols) {
            box.append(this.renderWrapRow(g.label, cards, picked, weakness, apply))
          }
          continue
        }

        // จอแคบบีบ 16 คอลัมน์ไม่ได้ — ให้เลื่อนแนวนอนแทนที่จะอัดจนตัวอักษรติดกัน
        const scroll = document.createElement('div')
        scroll.className = 'kana-scroll'

        const table = document.createElement('div')
        table.className = 'kana-table'
        table.style.gridTemplateColumns = `repeat(${cols.length}, minmax(2.4rem, 1fr))`
        table.style.gridTemplateRows = `auto repeat(${sub.slots}, auto)`

        // คอลัมน์แรกของกลุ่ม dakuten — เว้นช่องคั่นเหมือนตาราง 五十音図 ของจริง
        const firstDakuten = cols.findIndex(({ cards }) => cards[0]?.tags.includes('dakuten'))

        cols.forEach(({ g, cards }, i) => {
          const allOn = cards.every((c) => picked.has(c.id))
          const gap = i === firstDakuten

          const head = document.createElement('button')
          head.type = 'button'
          head.className = gap ? 'col-head is-gap' : 'col-head'
          head.title = `${g.label} — สลับทั้งคอลัมน์`
          head.setAttribute('aria-pressed', String(allOn))
          head.style.gridColumn = String(i + 1)
          head.style.gridRow = '1'
          head.addEventListener('click', () => {
            const next = new Set(picked)
            for (const c of cards) {
              if (allOn) next.delete(c.id)
              else next.add(c.id)
            }
            apply(next)
          })
          table.append(head)

          for (const card of cards) {
            const slot = Number(card.extra?.['slot'] ?? 0)
            const cell = document.createElement('button')
            cell.type = 'button'
            cell.className = gap ? 'kana-cell is-gap' : 'kana-cell'
            cell.setAttribute('aria-pressed', String(picked.has(card.id)))
            cell.style.gridColumn = String(i + 1)
            cell.style.gridRow = String(slot + 2)
            this.markWeakness(cell, weakness.get(card.id), card.back)

            const k = document.createElement('span')
            k.className = 'kana-cell-k'
            k.textContent = card.front
            const r = document.createElement('span')
            r.className = 'kana-cell-r'
            r.textContent = card.back

            cell.append(k, r)
            cell.addEventListener('click', () => {
              const next = new Set(picked)
              if (next.has(card.id)) next.delete(card.id)
              else next.add(card.id)
              apply(next)
            })
            table.append(cell)
          }
        })

        scroll.append(table)
        box.append(scroll)
      }
    }

    return box
  }

  /**
   * ระบายสถานะลงเซลล์ + ใส่ tooltip บอกเหตุผลและสถิติ
   * สีเป็นแค่ตัวชี้ให้กวาดตาเจอ · ตัวเลขจริงอยู่ใน title ตอนเอาเมาส์ไปวาง
   */
  private markWeakness(cell: HTMLElement, w: Weakness | undefined, meaning: string): void {
    if (!w) return

    const parts = [meaning, REASON_LABEL[w.reason]]
    if (w.seen > 0) {
      parts.push(`เจอ ${w.seen} ครั้ง · ผิด ${w.wrong}`)
      if (w.accuracy >= 0) parts.push(`ถูก ${Math.round(w.accuracy * 100)}%`)
      // ต่ำกว่า 100ms คือยังไม่ได้วัดจริง แสดงแล้วชวนสับสนมากกว่ามีประโยชน์
      if (w.avgMs >= 100) parts.push(`เฉลี่ย ${(w.avgMs / 1000).toFixed(1)} วิ`)
    }
    cell.title = parts.filter(Boolean).join(' · ')

    if (w.reason === 'new') cell.dataset['state'] = 'new'
    else if (w.score > 0) {
      cell.dataset['state'] = 'weak'
      // ผิดซ้ำจนเป็น leech ต้องเด่นกว่าตัวที่แค่ช้า
      if (w.reason === 'leech' || w.reason === 'lapsed') cell.dataset['severe'] = 'true'
    } else cell.dataset['state'] = 'ok'
  }

  /** แถวแบบ wrap สำหรับ deck ที่ไม่มีโครงตาราง — radical ไม่ได้เรียงตามสระ */
  private renderWrapRow(
    label: string,
    cards: Card[],
    picked: Set<string>,
    weakness: Map<string, Weakness>,
    apply: (next: Set<string>) => void,
  ): HTMLElement {
    const line = document.createElement('div')
    line.className = 'wrap-row'

    const head = document.createElement('button')
    head.type = 'button'
    head.className = 'wrap-head'
    head.textContent = label
    const allOn = cards.every((c) => picked.has(c.id))
    head.setAttribute('aria-pressed', String(allOn))
    head.addEventListener('click', () => {
      const next = new Set(picked)
      for (const c of cards) {
        if (allOn) next.delete(c.id)
        else next.add(c.id)
      }
      apply(next)
    })

    const cells = document.createElement('div')
    cells.className = 'wrap-cells'
    for (const card of cards) {
      const cell = document.createElement('button')
      cell.type = 'button'
      cell.className = 'kana-cell'
      cell.setAttribute('aria-pressed', String(picked.has(card.id)))
      this.markWeakness(cell, weakness.get(card.id), card.back)

      const k = document.createElement('span')
      k.className = 'kana-cell-k'
      k.textContent = card.front
      cell.append(k)

      cell.addEventListener('click', () => {
        const next = new Set(picked)
        if (next.has(card.id)) next.delete(card.id)
        else next.add(card.id)
        apply(next)
      })
      cells.append(cell)
    }

    line.append(head, cells)
    return line
  }

  private chipRow(
    label: string,
    items: Array<{
      tag: string
      label: string
      pressed: boolean
      jp?: boolean
      onClick: () => void
    }>,
  ): HTMLElement {
    const row = document.createElement('div')
    row.className = 'row'
    const l = document.createElement('span')
    l.className = 'row-label'
    l.textContent = label
    row.append(l)

    for (const it of items) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = it.jp ? 'chip chip-jp' : 'chip'
      b.textContent = it.label
      b.setAttribute('aria-pressed', String(it.pressed))
      b.addEventListener('click', it.onClick)
      row.append(b)
    }

    /*
     * บนจอแคบแถวนี้เลื่อนแนวนอนแทนที่จะขึ้นบรรทัดใหม่ ตัวที่เลือกอยู่จึงตกไป
     * อยู่นอกสายตาได้ — วัดบน 375px แล้วพบว่าโหมดที่กำลังใช้อยู่มองไม่เห็นเลย
     * ต้องเลื่อนมาเอง ไม่งั้นแถบนี้บอกไม่ได้ว่าตอนนี้อยู่โหมดไหน
     */
    queueMicrotask(() => {
      const active = row.querySelector<HTMLElement>('[aria-pressed="true"]')
      if (!active || row.scrollWidth <= row.clientWidth) return
      const box = row.getBoundingClientRect()
      const a = active.getBoundingClientRect()
      if (a.left >= box.left && a.right <= box.right) return
      row.scrollLeft += a.left - box.left - 12
    })
    return row
  }

  // ---------- สถิติ ----------

  private refreshStats(): void {
    const { done, total, label } = this.session.summary()
    const s = this.session.sessionStats()

    this.els.count.innerHTML = `<b>${done}</b>${label} / ${total}`
    this.els.bar.style.width = total > 0 ? `${(done / total) * 100}%` : '0'

    // คีย์ลัดต่างกันทุกโหมด — บอกของโหมดอื่นแล้วกดตามคือเสียเวลาเปล่า
    this.els.hint.innerHTML = MODE_HINT[this.currentMode()] ?? ''

    // สถิติถูก/ผิดไม่มีความหมายในโหมดเรียน — ค้างที่ 0 ตลอดโดยตั้งใจ ซ่อนไปเลยดีกว่าโชว์เลขปลอม
    const lesson = isLessonMode(this.currentMode())
    this.els.statRow.hidden = lesson
    if (lesson) return

    this.els.stTotal.textContent = String(s.total)
    this.els.stAcc.textContent = s.total > 0 ? `${Math.round((s.right / s.total) * 100)}%` : '—'
    this.els.stStreak.textContent = String(s.streak)
    this.els.stBest.textContent = String(s.best)
  }

  // ---------- ตั้งค่า ----------

  private renderPanel(): void {
    const p = this.els.panel
    p.replaceChildren()

    const h = document.createElement('h2')
    h.textContent = 'ตั้งค่า'
    p.append(h)

    // ฟอนต์
    const fontRow = document.createElement('div')
    fontRow.className = 'row'
    const fl = document.createElement('span')
    fl.className = 'row-label'
    fl.textContent = 'ฟอนต์'
    fontRow.append(fl)
    for (const f of FONTS) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'chip chip-jp'
      b.textContent = f.label
      b.title = f.where
      b.setAttribute('aria-pressed', String(settings.fonts.includes(f.id)))
      b.addEventListener('click', () => {
        const set = new Set(settings.fonts)
        if (set.has(f.id) && set.size > 1) set.delete(f.id)
        else set.add(f.id)
        settings.fonts = [...set]
        saveSettings()
        this.renderPanel()
      })
      fontRow.append(b)
    }
    p.append(fontRow)

    p.append(
      this.toggle('keypad สำหรับพิมพ์', settings.keypad ?? shouldUseKeypad(), (on) => {
        settings.keypad = on
        saveSettings()
        // สลับได้ทันทีโดยไม่ทิ้งข้อที่กำลังทำอยู่
        this.session.applyInputMode()
      }),
      this.toggle('เล่นเสียงอัตโนมัติ', settings.autoAudio, (on) => {
        settings.autoAudio = on
        saveSettings()
      }),
      // คู่กับเสียง — ใบใหม่ควรได้ทั้งเห็นวิธีเขียนและได้ยินเสียงโดยไม่ต้องกด
      // แต่ปิดได้เหมือนกัน สำหรับคนที่อยากลองนึกรูปเองก่อน
      this.toggle('วาดลำดับขีดอัตโนมัติในโหมดเรียน', settings.autoStroke, (on) => {
        settings.autoStroke = on
        saveSettings()
      }),
      this.toggle('โชว์คำอ่าน on/kun ตอนเฉลย', settings.showReadings, (on) => {
        settings.showReadings = on
        saveSettings()
      }),
    )

    const readingNote = document.createElement('div')
    readingNote.className = 'note'
    readingNote.textContent =
      'NOTES.md แนะให้แยกจำความหมายกับคำอ่านคนละรอบ — ปิดตัวนี้ถ้าอยากทำตามนั้นเป๊ะ'
    p.append(readingNote)

    if (!hasJapaneseVoice()) {
      const warn = document.createElement('div')
      warn.className = 'note'
      warn.textContent =
        'เครื่องนี้ยังไม่มีเสียงภาษาญี่ปุ่นของระบบ — เสียงจะมาจากไฟล์ที่ติดมากับแอปเท่านั้น'
      p.append(warn)
    }

    // ใบใหม่ต่อวัน
    const np = document.createElement('div')
    np.className = 'field'
    np.innerHTML = `<span>ใบใหม่ต่อรอบในโหมดเรียน</span>`
    const input = document.createElement('input')
    input.type = 'number'
    input.min = '0'
    input.max = '100'
    input.value = String(settings.newPerDay)
    input.className = 'answer-input'
    input.style.width = '80px'
    input.style.fontSize = '0.95rem'
    input.addEventListener('change', () => {
      settings.newPerDay = Math.max(0, Math.min(100, Number(input.value) || 0))
      saveSettings()
      void this.session.reload()
    })
    np.append(input)
    p.append(np)

    // สำรองข้อมูล
    const backup = document.createElement('div')
    backup.className = 'row'
    const bl = document.createElement('span')
    bl.className = 'row-label'
    bl.textContent = 'ข้อมูล'
    backup.append(bl)

    const exportBtn = document.createElement('button')
    exportBtn.className = 'chip'
    exportBtn.textContent = 'บันทึกไฟล์สำรอง'
    exportBtn.addEventListener('click', () => void downloadBackup())

    const importBtn = document.createElement('button')
    importBtn.className = 'chip'
    importBtn.textContent = 'อ่านไฟล์สำรอง'
    const file = document.createElement('input')
    file.type = 'file'
    file.accept = 'application/json'
    file.hidden = true
    file.addEventListener('change', async () => {
      const f = file.files?.[0]
      if (!f) return
      try {
        const data = await readBackupFile(f)
        const r = await importBackup(data)
        alert(`นำเข้าแล้ว — ความคืบหน้า ${r.progress} ใบ · kana ${r.drill} ตัว`)
        await this.session.reload()
        this.refreshStats()
      } catch (err) {
        alert(err instanceof Error ? err.message : 'อ่านไฟล์ไม่สำเร็จ')
      }
      file.value = ''
    })
    importBtn.addEventListener('click', () => file.click())

    const resetBtn = document.createElement('button')
    resetBtn.className = 'chip'
    resetBtn.textContent = 'ล้างความคืบหน้า'
    resetBtn.addEventListener('click', async () => {
      if (!confirm('ล้างความคืบหน้าทั้งหมด? กู้คืนไม่ได้ — บันทึกไฟล์สำรองไว้ก่อนดีกว่า')) return
      await clearAll()
      await this.session.reload()
      this.refreshStats()
    })

    backup.append(exportBtn, importBtn, file, resetBtn)
    p.append(backup)

    p.append(this.renderSyncSection())

    const credit = document.createElement('div')
    credit.className = 'note'
    // สองแหล่งคนละเวอร์ชันของ CC BY-SA — 4.0 กับ 3.0 เขียนรวมกันไม่ได้
    credit.innerHTML = `ข้อมูลคันจิและพจนานุกรมจาก
      <a href="https://www.edrdg.org/edrdg/licence.html" target="_blank" rel="noopener">EDRDG</a>
      (CC BY-SA 4.0) · ลำดับขีดจาก
      <a href="https://kanjivg.tagaini.net/" target="_blank" rel="noopener">KanjiVG</a>
      © Ulrich Apel (CC BY-SA 3.0) · ดูรายละเอียดใน DATA-SOURCES.md`
    p.append(credit)
  }

  // ---------- ซิงก์ข้ามเครื่อง ----------

  private renderSyncSection(): HTMLElement {
    const box = document.createElement('div')
    box.className = 'sync-box'

    const h = document.createElement('h2')
    h.textContent = 'ซิงก์ข้ามเครื่อง'
    box.append(h)

    const field = (
      label: string,
      value: string,
      type: string,
      placeholder: string,
      onInput: (v: string) => void,
    ): HTMLElement => {
      const wrap = document.createElement('label')
      wrap.className = 'sync-field'
      const lb = document.createElement('span')
      lb.textContent = label
      const input = document.createElement('input')
      input.type = type
      input.value = value
      input.placeholder = placeholder
      input.autocomplete = 'off'
      input.spellcheck = false
      input.addEventListener('input', () => onInput(input.value.trim()))
      wrap.append(lb, input)
      return wrap
    }

    box.append(
      field('ที่อยู่ Worker', settings.syncUrl, 'url', 'https://jpt-sync.xxx.workers.dev', (v) => {
        settings.syncUrl = v
        saveSettings()
      }),
      field('รหัสลับ', settings.syncKey, 'password', 'ที่ตั้งไว้ตอน wrangler secret put', (v) => {
        settings.syncKey = v
        saveSettings()
      }),
    )

    const row = document.createElement('div')
    row.className = 'row'
    const status = document.createElement('span')
    status.className = 'note sync-status'
    status.textContent = settings.syncedAt
      ? `ล่าสุด ${new Date(settings.syncedAt).toLocaleString('th-TH')}`
      : 'ยังไม่เคยซิงก์'

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'chip'
    btn.textContent = 'ซิงก์เดี๋ยวนี้'
    btn.addEventListener('click', async () => {
      btn.disabled = true
      status.textContent = 'กำลังซิงก์…'
      try {
        const r = await syncNow({ url: settings.syncUrl, key: settings.syncKey })
        settings.syncedAt = r.at
        saveSettings()
        status.textContent = `รับมา ${r.pulled} · ส่งไป ${r.pushed} · v${r.version}`
        await this.refilter()
      } catch (err) {
        status.textContent = err instanceof Error ? err.message : 'ซิงก์ไม่สำเร็จ'
      }
      btn.disabled = false
    })

    row.append(btn, status)
    box.append(row)

    const help = document.createElement('div')
    help.className = 'note'
    help.textContent =
      'ตั้งค่าเหมือนกันทุกเครื่อง แล้วมันจะซิงก์เองตอนเปิดแอปและตอนสลับไปแอปอื่น · วิธี deploy อยู่ใน worker/sync.js'
    box.append(help)

    return box
  }

  /**
   * ซิงก์เงียบๆ ตอนเปิดแอปและตอนสลับออก
   * ตอนสลับออกสำคัญกว่า — เป็นจังหวะที่เพิ่งฝึกเสร็จ ข้อมูลใหม่ที่สุดอยู่ตรงนี้
   */
  private setupAutoSync(): void {
    const run = (): void => {
      if (!settings.syncUrl || !settings.syncKey) return
      void syncNow({ url: settings.syncUrl, key: settings.syncKey })
        .then((r) => {
          settings.syncedAt = r.at
          saveSettings()
          if (r.pulled > 0) void this.refilter()
        })
        .catch(() => {
          /* ออฟไลน์หรือ Worker ล่ม — ไม่ควรขวางการฝึก ค่อยลองใหม่รอบหน้า */
        })
    }

    run()
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') run()
    })
  }

  private toggle(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
    const row = document.createElement('label')
    row.className = 'field'
    const span = document.createElement('span')
    span.textContent = label
    const input = document.createElement('input')
    input.type = 'checkbox'
    input.checked = value
    input.addEventListener('change', () => onChange(input.checked))
    row.append(span, input)
    return row
  }
}
